import { useCallback, useEffect, useState } from 'react'
import { commitPlan, subscribeBlocksInRange } from '../data/blocks'
import {
  planCreate,
  planDelete,
  planEdit,
  planEditMeta,
  planMove,
  planMoveBoundary,
  planResize,
  planSplit,
} from '../data/blockPlan'
import { newId } from '../lib/id'
import { recordTagUse, subscribeTags } from '../data/tags'
import type { Block } from '../data/types'
import type { BlockEdit } from './BlockEditor'

/** Live blocks overlapping a range, plus the mutations the timeline needs. */
export function useRangeBlocks(uid: string, from: Date, to: Date) {
  const [blocks, setBlocks] = useState<Block[]>([])
  const [error, setError] = useState<string | null>(null)
  const fromMs = from.getTime()
  const toMs = to.getTime()

  useEffect(
    () =>
      subscribeBlocksInRange(uid, new Date(fromMs), new Date(toMs), setBlocks, (cause) =>
        setError(cause.message),
      ),
    [uid, fromMs, toMs],
  )

  const run = useCallback(
    async (plan: ReturnType<typeof planCreate>) => {
      if (!plan.ok) {
        setError(plan.message)
        return
      }
      setError(null)
      try {
        await commitPlan(uid, plan)
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Could not save that change.')
      }
    },
    [uid],
  )

  const neighboursOf = useCallback(
    (block: Block) => {
      const sorted = [...blocks].sort((a, b) => a.start - b.start)
      const index = sorted.findIndex((candidate) => candidate.id === block.id)
      return {
        previous: index > 0 ? (sorted[index - 1] ?? null) : null,
        next: index >= 0 ? (sorted[index + 1] ?? null) : null,
      }
    },
    [blocks],
  )

  const create = useCallback(
    (range: { start: number; end: number }, categoryId: string) =>
      run(planCreate(blocks, { ...range, categoryId, newId: newId(), source: 'manual' })),
    [blocks, run],
  )

  const resize = useCallback(
    (block: Block, bounds: { start?: number; end?: number }) =>
      run(planResize(block, bounds, neighboursOf(block))),
    [neighboursOf, run],
  )

  const move = useCallback(
    (block: Block, delta: number) => run(planMove(block, delta, blocks)),
    [blocks, run],
  )

  const moveBoundary = useCallback(
    (previous: Block, next: Block, at: number) => run(planMoveBoundary(previous, next, at)),
    [run],
  )

  const edit = useCallback(
    async (block: Block, next: BlockEdit) => {
      await run(planEdit(block, next, neighboursOf(block)))
      if (next.tags.length > 0) void recordTagUse(uid, next.tags)
    },
    [neighboursOf, run, uid],
  )

  /** The right-click menu's one-field changes, which never touch the bounds. */
  const quickPatch = useCallback(
    async (block: Block, patch: Partial<Pick<Block, 'categoryId' | 'note' | 'tags'>>) => {
      await run(planEditMeta(block, patch))
      if (patch.tags?.length) void recordTagUse(uid, patch.tags)
    },
    [run, uid],
  )

  const split = useCallback(
    (block: Block, at: number) => run(planSplit(block, at, newId())),
    [run],
  )

  const remove = useCallback((block: Block) => run(planDelete(block)), [run])

  /**
   * The free stretch a block may occupy: up to its neighbours, and unbounded
   * where it has none. The editor validates typed times against this before it
   * will let a save through.
   */
  const limitsFor = useCallback(
    (block: Block) => {
      const { previous, next } = neighboursOf(block)
      return {
        from: previous?.end ?? Number.NEGATIVE_INFINITY,
        to: next?.start ?? Number.POSITIVE_INFINITY,
      }
    },
    [neighboursOf],
  )

  return {
    blocks,
    error,
    create,
    resize,
    move,
    moveBoundary,
    neighboursOf,
    limitsFor,
    edit,
    quickPatch,
    split,
    remove,
    clearError: () => setError(null),
  }
}

/** Every tag used so far, most-used first — the autocomplete behind the tag inputs. */
export function useKnownTags(uid: string): string[] {
  const [tags, setTags] = useState<string[]>([])
  useEffect(() => subscribeTags(uid, setTags, () => setTags([])), [uid])
  return tags
}
